import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Upload, Download, Search, Edit, Trash2, 
  AlertTriangle, FileSpreadsheet, Settings2, GripVertical
} from 'lucide-react';

import { API } from '../lib/apiBase';

const DEFAULT_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'surname', label: 'Surname', required: true },
  { key: 'phone_number', label: 'Phone', required: true },
  { key: 'child_first_name', label: 'Child Name', required: false },
  { key: 'child_birthdate', label: 'Birthdate', required: false },
  { key: 'diaper_size', label: 'Diaper Size', required: false },
];

export default function BeneficiariesPage() {
  const { organization, refreshOrganization, isAdmin, isOperator } = useAuth();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [tableFields, setTableFields] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    surname: '',
    phone_number: '',
    child_first_name: '',
    child_birthdate: '',
    diaper_size: '',
    custom_fields: {}
  });

  const [newField, setNewField] = useState({ name: '', field_type: 'text' });

  // Get all available fields (default + custom)
  const getAllFields = useCallback(() => {
    const fields = [...DEFAULT_FIELDS];
    customFields.forEach(cf => {
      fields.push({ key: `custom_${cf.name}`, label: cf.name, required: false, isCustom: true, customName: cf.name });
    });
    return fields;
  }, [customFields]);

  const fetchBeneficiaries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/beneficiaries`);
      setBeneficiaries(response.data);
    } catch (error) {
      toast.error('Error loading beneficiaries');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomFields = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/custom-fields`);
      setCustomFields(response.data);
    } catch {
      // Silent: custom fields are optional; UI still works without them
    }
  }, []);

  useEffect(() => {
    fetchBeneficiaries();
    fetchCustomFields();
  }, [fetchBeneficiaries, fetchCustomFields]);

  // Initialize table fields from organization settings, merging in any newly-created
  // custom fields that haven't been persisted to organization.table_fields yet.
  useEffect(() => {
    const allFields = getAllFields();
    const allKeys = allFields.map(f => f.key);

    const saved = organization?.table_fields;
    if (saved && saved.length > 0) {
      // Keep saved order/visibility but drop entries whose field no longer exists
      const filtered = saved.filter(f => allKeys.includes(f.key));
      const savedKeys = new Set(filtered.map(f => f.key));
      // Append any field present in the system but missing from saved settings (e.g. newly added custom field)
      const appended = allFields
        .filter(f => !savedKeys.has(f.key))
        .map(f => ({ key: f.key, visible: true }));
      setTableFields([...filtered, ...appended]);
    } else {
      // Default: show all fields in default order
      setTableFields(allFields.map(f => ({ key: f.key, visible: true })));
    }
  }, [organization, getAllFields]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      surname: '',
      phone_number: '',
      child_first_name: '',
      child_birthdate: '',
      diaper_size: '',
      custom_fields: {}
    });
    setDuplicateWarning(null);
  };

  const handleAddBeneficiary = async () => {
    try {
      const response = await axios.post(`${API}/beneficiaries`, formData);

      if (response.data.duplicates?.length > 0) {
        setDuplicateWarning(response.data.duplicates);
        response.data.duplicates.forEach(dup => {
          if (dup.same_organization) {
            toast.warning(
              `Already registered in your organization: ${dup.beneficiary_name}${dup.phone_number ? ' (' + dup.phone_number + ')' : ''}`,
              { duration: 8000, className: 'toast-duplicate-internal' }
            );
          } else if (dup.type === 'phone') {
            toast.warning(
              `Warning: ${dup.beneficiary_name} with same phone number found in ${dup.organization_name}. Contact: ${dup.organization_email || dup.organization_phone}`,
              { duration: 8000, className: 'toast-duplicate-phone' }
            );
          } else {
            toast.warning(
              `Note: ${dup.beneficiary_name} with same name found in ${dup.organization_name}. Contact: ${dup.organization_email || dup.organization_phone}`,
              { duration: 6000, className: 'toast-duplicate-name' }
            );
          }
        });
      }

      toast.success('Beneficiary added successfully!');
      setShowAddModal(false);
      resetForm();
      fetchBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding beneficiary');
    }
  };

  const handleEditBeneficiary = async () => {
    try {
      await axios.put(`${API}/beneficiaries/${selectedBeneficiary.id}`, formData);
      toast.success('Beneficiary updated successfully!');
      setShowEditModal(false);
      setSelectedBeneficiary(null);
      resetForm();
      fetchBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating beneficiary');
    }
  };

  const handleDeleteBeneficiary = async (id) => {
    if (!window.confirm('Are you sure you want to delete this beneficiary?\n\nThe associated QR code will be invalidated and can no longer be used for check-in.')) return;
    try {
      await axios.delete(`${API}/beneficiaries/${id}`);
      toast.success('Beneficiary deleted. Its QR code has been invalidated.', { duration: 6000 });
      fetchBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting beneficiary');
    }
  };

  const handleAddCustomField = async () => {
    if (!newField.name.trim()) {
      toast.error('Please enter a field name');
      return;
    }
    try {
      await axios.post(`${API}/custom-fields`, newField);
      toast.success('Custom field added!');
      setShowFieldModal(false);
      setNewField({ name: '', field_type: 'text' });
      fetchCustomFields();
      // useEffect will reconcile tableFields once customFields refresh arrives
    } catch (error) {
      toast.error('Error adding custom field');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a .csv file');
      e.target.value = '';
      return;
    }

    const fd = new FormData();
    fd.append('file', file);

    const toastId = toast.loading(`Importing ${file.name}...`);
    try {
      const response = await axios.post(`${API}/beneficiaries/import-csv`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { imported = 0, errors = [], duplicates = [], total_rows = 0 } = response.data;

      toast.dismiss(toastId);

      if (imported > 0) {
        toast.success(`Imported ${imported} beneficiary${imported === 1 ? '' : 'ies'}${total_rows ? ` of ${total_rows}` : ''}`);
      } else {
        toast.error('No beneficiaries imported. Check required columns: first_name, surname, phone_number');
      }

      if (duplicates.length > 0) {
        duplicates.slice(0, 5).forEach(d => {
          toast.warning(`Row ${d.row || '-'}: ${d.beneficiary} – possible duplicate`, { duration: 5000 });
        });
        if (duplicates.length > 5) {
          toast.warning(`+${duplicates.length - 5} more duplicate warnings`, { duration: 5000 });
        }
      }

      if (errors.length > 0) {
        errors.slice(0, 3).forEach(err => {
          const msg = typeof err === 'string' ? err : `Row ${err.row}: ${err.error}`;
          toast.error(msg, { duration: 6000 });
        });
        if (errors.length > 3) {
          toast.error(`+${errors.length - 3} more errors during import`, { duration: 6000 });
        }
      }

      setShowImportModal(false);
      fetchBeneficiaries();
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(error.response?.data?.detail || 'Error importing file');
    } finally {
      e.target.value = '';
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/beneficiaries-csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'beneficiaries.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV file downloaded!');
    } catch (error) {
      toast.error('Error downloading file');
    }
  };

  const openEditModal = (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setFormData({
      first_name: beneficiary.first_name || '',
      surname: beneficiary.surname || '',
      phone_number: beneficiary.phone_number || '',
      child_first_name: beneficiary.child_first_name || '',
      child_birthdate: beneficiary.child_birthdate || '',
      diaper_size: beneficiary.diaper_size || '',
      custom_fields: beneficiary.custom_fields || {}
    });
    setShowEditModal(true);
  };

  // Column configuration handlers
  const toggleFieldVisibility = (key) => {
    setTableFields(prev => prev.map(f => 
      f.key === key ? { ...f, visible: !f.visible } : f
    ));
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newFields = [...tableFields];
    const draggedField = newFields[draggedItem];
    newFields.splice(draggedItem, 1);
    newFields.splice(index, 0, draggedField);
    setTableFields(newFields);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const saveColumnSettings = async () => {
    try {
      await axios.put(`${API}/organizations/mine`, { table_fields: tableFields });
      await refreshOrganization();
      toast.success('Column settings saved!');
      setShowColumnModal(false);
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  const resetColumnSettings = () => {
    const allFields = getAllFields();
    setTableFields(allFields.map(f => ({ key: f.key, visible: true })));
  };

  const downloadCsvTemplate = () => {
    const headers = ['first_name', 'surname', 'phone_number', 'child_first_name', 'child_birthdate', 'diaper_size'];
    const customHeaders = customFields.map(f => f.name);
    const allHeaders = [...headers, ...customHeaders];
    const example = ['John', 'Doe', '+391234567890', 'Anna', '2020-05-15', '4', ...customHeaders.map(() => '')];
    const csv = `${allHeaders.join(',')}\n${example.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'beneficiaries_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const filteredBeneficiaries = beneficiaries.filter(b =>
    b.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.phone_number?.includes(searchTerm)
  );

  // Get visible fields in order
  const visibleFields = tableFields
    .filter(f => f.visible)
    .map(f => {
      const allFields = getAllFields();
      const fieldInfo = allFields.find(af => af.key === f.key);
      return fieldInfo || { key: f.key, label: f.key };
    });

  // Get field value from beneficiary
  const getFieldValue = (beneficiary, field) => {
    if (field.isCustom) {
      return beneficiary.custom_fields?.[field.customName] || '-';
    }
    return beneficiary[field.key] || '-';
  };

  return (
    <div className="space-y-6" data-testid="beneficiaries-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Beneficiaries</h1>
            <span
              className="inline-flex items-center rounded-full bg-[#1A1A1A] text-white px-3 py-1 text-sm font-semibold"
              data-testid="beneficiaries-total-count"
            >
              {beneficiaries.length} registered
            </span>
          </div>
          <p className="text-[#595959] mt-1 text-sm md:text-base">
            Manage your organization's beneficiaries
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="md:size-default"
            onClick={() => setShowColumnModal(true)}
            data-testid="configure-columns-button"
          >
            <Settings2 className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Columns</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="md:size-default"
            onClick={() => setShowImportModal(true)}
            data-testid="import-csv-button"
          >
            <Upload className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Import CSV</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="md:size-default"
            onClick={handleExportCSV}
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Export CSV</span>
          </Button>
          {isOperator && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="md:size-default"
                onClick={() => setShowFieldModal(true)}
                data-testid="add-field-button"
              >
                <Plus className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">New Field</span>
              </Button>
              <Button
                size="sm"
                className="bg-[#4A5D4E] hover:bg-[#3D4D40] md:size-default"
                onClick={() => { resetForm(); setShowAddModal(true); }}
                data-testid="add-beneficiary-button"
              >
                <Plus className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Add Beneficiary</span>
                <span className="md:hidden ml-1">Add</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
        <Input
          placeholder="Search by name, surname or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Custom Fields Info */}
      {customFields.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-[#8C8C8C]">Custom fields:</span>
          {customFields.map(field => (
            <span key={field.id} className="badge badge-info">{field.name}</span>
          ))}
        </div>
      )}

      {/* Mobile Card List (hidden on md+) */}
      <div className="md:hidden space-y-2" data-testid="beneficiaries-mobile-list">
        {loading ? (
          <Card className="border-[#E5E5E5]"><CardContent className="py-8 text-center text-[#8C8C8C]">Loading...</CardContent></Card>
        ) : filteredBeneficiaries.length === 0 ? (
          <Card className="border-[#E5E5E5]"><CardContent className="py-8 text-center text-[#8C8C8C]">No beneficiaries found</CardContent></Card>
        ) : (
          filteredBeneficiaries.map((beneficiary) => (
            <Card key={beneficiary.id} className="border border-[#E5E5E5]" data-testid={`beneficiary-row-${beneficiary.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A1A1A] text-sm break-words">
                      {beneficiary.first_name} {beneficiary.surname}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[#595959]">
                      {beneficiary.phone_number && <span className="font-mono">{beneficiary.phone_number}</span>}
                      {beneficiary.child_first_name && <span>Child: {beneficiary.child_first_name}</span>}
                      {beneficiary.child_birthdate && <span>{beneficiary.child_birthdate}</span>}
                      {beneficiary.diaper_size && <span>Size {beneficiary.diaper_size}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {isOperator && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditModal(beneficiary)}
                        data-testid={`edit-${beneficiary.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-[#D34848] hover:text-[#C62828]"
                        onClick={() => handleDeleteBeneficiary(beneficiary.id)}
                        data-testid={`delete-${beneficiary.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table (hidden on mobile) */}
      <Card className="border-[#E5E5E5] hidden md:block">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F0EFEA]">
                  {visibleFields.map(field => (
                    <TableHead key={field.key} className="font-medium">{field.label}</TableHead>
                  ))}
                  <TableHead className="font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleFields.length + 1} className="text-center py-8 text-[#8C8C8C]">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredBeneficiaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleFields.length + 1} className="text-center py-8 text-[#8C8C8C]">
                      No beneficiaries found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredBeneficiaries.map((beneficiary) => (
                    <TableRow 
                      key={beneficiary.id}
                      data-testid={`beneficiary-row-desk-${beneficiary.id}`}
                    >
                      {visibleFields.map(field => (
                        <TableCell key={field.key}>{getFieldValue(beneficiary, field)}</TableCell>
                      ))}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {isOperator && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(beneficiary)}
                              data-testid={`edit-desk-${beneficiary.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-[#D34848] hover:text-[#C62828]"
                              onClick={() => handleDeleteBeneficiary(beneficiary.id)}
                              data-testid={`delete-desk-${beneficiary.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Column Configuration Modal */}
      <Dialog open={showColumnModal} onOpenChange={setShowColumnModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Configure Columns</DialogTitle>
            <DialogDescription>Select which fields to show and drag to reorder</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {tableFields.map((field, index) => {
                const allFields = getAllFields();
                const fieldInfo = allFields.find(f => f.key === field.key);
                const label = fieldInfo?.label || field.key;
                
                return (
                  <div
                    key={field.key}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E5] hover:bg-[#FAFAF8] cursor-move ${
                      draggedItem === index ? 'opacity-50 bg-[#E9EFEA]' : ''
                    }`}
                  >
                    <GripVertical className="w-4 h-4 text-[#8C8C8C]" />
                    <Checkbox
                      checked={field.visible}
                      onCheckedChange={() => toggleFieldVisibility(field.key)}
                      data-testid={`column-checkbox-${field.key}`}
                    />
                    <span className="flex-1 text-sm">{label}</span>
                    {fieldInfo?.isCustom && (
                      <span className="text-xs text-[#8C8C8C] bg-[#E9EFEA] px-2 py-0.5 rounded">Custom</span>
                    )}
                  </div>
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-4 text-[#595959]"
              onClick={resetColumnSettings}
            >
              Reset to default
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowColumnModal(false)}>Cancel</Button>
            <Button 
              className="bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={saveColumnSettings}
              data-testid="save-columns-button"
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {showEditModal ? 'Edit Beneficiary' : 'New Beneficiary'}
            </DialogTitle>
            <DialogDescription>
              {showEditModal ? 'Edit the selected beneficiary data' : 'Enter the new beneficiary data'}
            </DialogDescription>
          </DialogHeader>
          
          {duplicateWarning && (
            <div className="bg-[#FFF8E1] border border-[#E0A838] rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#F57F17] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#1A1A1A]">Possible duplicates found</p>
                  {duplicateWarning.map((dup) => (
                    <p key={`${dup.type}-${dup.beneficiary_name}-${dup.organization_name}`} className="text-sm text-[#595959] mt-1">
                      <span className={dup.same_organization ? 'font-semibold text-[#B45309]' : ''}>
                        {dup.beneficiary_name}
                      </span>{' '}
                      {dup.same_organization
                        ? '— already registered in your organization'
                        : <>in {dup.organization_name} ({dup.type === 'phone' ? 'same phone' : 'same name'})</>}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="grid gap-4 max-h-[400px] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Surname *</Label>
                <Input
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  data-testid="input-surname"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                data-testid="input-phone"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Child Name</Label>
                <Input
                  value={formData.child_first_name}
                  onChange={(e) => setFormData({ ...formData, child_first_name: e.target.value })}
                  data-testid="input-child-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Birthdate</Label>
                <Input
                  type="date"
                  value={formData.child_birthdate}
                  onChange={(e) => setFormData({ ...formData, child_birthdate: e.target.value })}
                  data-testid="input-child-birthdate"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Diaper Size</Label>
              <Input
                value={formData.diaper_size}
                onChange={(e) => setFormData({ ...formData, diaper_size: e.target.value })}
                placeholder="e.g. 4, 5, 6"
                data-testid="input-diaper-size"
              />
            </div>
            
            {customFields.length > 0 && (
              <div className="border-t pt-4 mt-2">
                <p className="text-sm font-medium mb-3">Custom Fields</p>
                <div className="grid gap-3">
                  {customFields.map(field => (
                    <div key={field.id} className="space-y-2">
                      <Label>{field.name}</Label>
                      <Input
                        value={formData.custom_fields[field.name] || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          custom_fields: { ...formData.custom_fields, [field.name]: e.target.value }
                        })}
                        data-testid={`input-custom-${field.name}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              className="bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={showEditModal ? handleEditBeneficiary : handleAddBeneficiary}
              data-testid="save-beneficiary-button"
            >
              {showEditModal ? 'Save Changes' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Field Modal */}
      <Dialog open={showFieldModal} onOpenChange={setShowFieldModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Add Custom Field</DialogTitle>
            <DialogDescription>Create a new field to add to beneficiary data</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Field Name</Label>
              <Input
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                placeholder="e.g. Nationality, Allergies..."
                data-testid="input-field-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldModal(false)}>Cancel</Button>
            <Button 
              className="bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={handleAddCustomField}
              data-testid="save-field-button"
            >
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Import Beneficiaries from CSV</DialogTitle>
            <DialogDescription>Upload a CSV file with beneficiary data</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="dropzone relative">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-[#8C8C8C]" />
              <p className="text-[#595959] mb-2">Drag the CSV file here or click to select</p>
              <p className="text-xs text-[#8C8C8C]">
                Required columns: <span className="font-medium">first_name, surname, phone_number</span>
              </p>
              <p className="text-xs text-[#8C8C8C] mt-1">
                Optional: child_first_name, child_birthdate, diaper_size + any custom field
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleImportCSV}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="csv-file-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={downloadCsvTemplate}
              data-testid="download-csv-template-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV template
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
